/**
 * Proof video limits and validation (spec §9.5 / §13.5).
 * Duration limit 60 seconds, size limit 50 MB, with Dutch user feedback.
 */

export const PROOF_MAX_DURATION_SECONDS = 60
export const PROOF_MAX_SIZE_BYTES = 50 * 1024 * 1024

const MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
] as const

/** In-app camera capture is possible only when both APIs exist. */
export function isInAppRecordingSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  )
}

/** First container/codec combination this browser can record, or null. */
export function pickSupportedMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return null
  }
  for (const candidate of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate
  }
  return null
}

export function extensionForMimeType(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'mp4'
  if (mimeType.includes('webm')) return 'webm'
  return 'webm'
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`
  return `${bytes} B`
}

export function formatDuration(seconds: number): string {
  const clamped = Math.max(0, Math.round(seconds))
  const mins = Math.floor(clamped / 60)
  const secs = String(clamped % 60).padStart(2, '0')
  return `${mins}:${secs}`
}

export type ProofValidationError =
  | 'too_large'
  | 'too_long'
  | 'not_video'
  | 'unreadable'

export const PROOF_VALIDATION_COPY: Record<ProofValidationError, string> = {
  too_large: `De video is groter dan ${formatBytes(PROOF_MAX_SIZE_BYTES)}. Neem een kortere video op of kies een ander bestand.`,
  too_long: `De video duurt langer dan ${PROOF_MAX_DURATION_SECONDS} seconden. Neem opnieuw op binnen de limiet.`,
  not_video: 'Dit bestand is geen video. Kies een videobestand.',
  unreadable: 'Deze video kan niet worden gelezen. Probeer een ander bestand.',
}

/**
 * Reads the duration of a video file via a detached <video> element.
 * Resolves null when the metadata cannot be read. Always revokes the URL.
 */
export function readVideoDurationSeconds(file: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve(null)
      return
    }
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    let settled = false
    const finish = (value: number | null) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      video.removeAttribute('src')
      video.load()
      URL.revokeObjectURL(url)
      resolve(value)
    }
    const timer = window.setTimeout(() => finish(null), 5000)
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      // Some browsers report Infinity for stream-recorded blobs; treat as unknown.
      if (!Number.isFinite(video.duration)) {
        finish(null)
        return
      }
      finish(video.duration)
    }
    video.onerror = () => finish(null)
    video.src = url
  })
}

/**
 * Validates a chosen/recorded file against the product limits. Duration is
 * checked only when known — in-app recordings are time-limited at capture.
 */
export async function validateProofFile(
  file: Blob,
  options: { checkDuration: boolean },
): Promise<ProofValidationError | null> {
  if (file.size > PROOF_MAX_SIZE_BYTES) return 'too_large'
  if (file.type && !file.type.startsWith('video/')) return 'not_video'
  if (options.checkDuration) {
    const duration = await readVideoDurationSeconds(file)
    if (duration !== null && duration > PROOF_MAX_DURATION_SECONDS + 0.5) return 'too_long'
  }
  return null
}
